import { useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { CandidateSpeciesCard } from "@/app/components/CandidateSpeciesCard";
import { ScreenHead } from "@/app/components/ScreenHead";
import { Button } from "@/app/components/system/Button";
import { selectSearchedSpecies } from "@/app/lib/client";
import { PLANT_PALETTE, hashStr } from "@/palette";
import type { PlantCandidate } from "@/server/lib/types";

interface PlantCandidatesState {
  candidates: PlantCandidate[];
  photo: { id: string; url: string } | null;
  defaultPotId?: string;
}

export function PlantCandidatesScreen() {
  const location = useLocation();
  const state = location.state as PlantCandidatesState | null;
  if (!state?.candidates?.length) return <Navigate to="/add" replace />;
  return <PlantCandidatesInner {...state} />;
}

function PlantCandidatesInner({ candidates, photo, defaultPotId }: PlantCandidatesState) {
  const navigate = useNavigate();
  // Monotonic token so a late /species-select response is ignored if the user
  // has navigated away or started another pick.
  const pickSeqRef = useRef(0);
  const [pickingKey, setPickingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addPath = "/add" + (defaultPotId ? `?potId=${defaultPotId}` : "");

  const onPick = async (cand: PlantCandidate) => {
    if (pickingKey) return;
    const seq = ++pickSeqRef.current;
    setPickingKey(cand.scientific);
    setError(null);
    try {
      // Resolving the pick generates care recommendations and upserts the
      // species — the same endpoint the Search Species flow uses.
      const { species } = await selectSearchedSpecies({
        scientific: cand.scientific,
        common: cand.common || cand.scientific,
      });
      if (seq !== pickSeqRef.current) return; // user abandoned mid-pick
      if (!species) throw new Error("Couldn’t load that species");
      navigate("/scan-result", {
        state: { result: species, defaultPotId, fromKnown: false, fromManual: false, source: "photo", photoUpload: photo },
      });
    } catch (err) {
      if (seq !== pickSeqRef.current) return;
      setError((err as Error).message || "Couldn’t add that species");
    } finally {
      if (seq === pickSeqRef.current) setPickingKey(null);
    }
  };

  return (
    <div className="screen pad">
      <ScreenHead
        title="Identify your plant"
        subtitle="Pick the closest match — we'll fill in its care details."
        onBack={() => navigate(addPath)} />

      {photo?.url && (
        <div className="grid place-items-center pt-2 pb-5 mb-5.5 bg-cream-2 rounded-[18px] border border-line">
          <img src={photo.url} alt="Your plant" className="w-[140px] h-[140px] rounded-2xl object-cover" />
        </div>
      )}

      {error && <p className="font-sans text-[13px] text-ink italic px-1 py-2">{error}</p>}

      <div className={`flex flex-col gap-2.5${pickingKey ? " pointer-events-none" : ""}`}>
        {candidates.map((cand) => {
          const title = cand.common || cand.scientific;
          const isPicking = pickingKey === cand.scientific;
          const dimmed = pickingKey && !isPicking;
          return (
            <div key={cand.scientific} className={`relative${dimmed ? " opacity-50" : ""}`}>
              <CandidateSpeciesCard
                species={{
                  scientific: cand.scientific,
                  common: cand.common,
                  color: PLANT_PALETTE[hashStr(cand.scientific) % PLANT_PALETTE.length]!,
                  initial: (title[0] || "?").toUpperCase(),
                  labelImageUrl: null,
                }}
                reasoning={cand.reasoning}
                onClick={() => onPick(cand)}
              />
              {isPicking && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-sans text-[12.5px] text-ink-3 italic bg-paper pl-2">
                  Adding…
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <Button variant="secondary" className="w-full" disabled={!!pickingKey} onClick={() => navigate(addPath)}>
          None of these — re-scan
        </Button>
      </div>
    </div>
  );
}
